import { useTheme } from '@emotion/react';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { DropdownWrapperMedium } from '../../../../common/FormElementWrappers/FormElementWrappers.styles';
import { H2 } from '../../../../common/Headings/Headings.styles';
import Button from '../../../../components/Button/Button';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import Radio from '../../../../components/Radio/Radio';
import Table from '../../../../components/Table/Table';
import { showToast } from '../../../../components/Toast/Toast';
import { ToastTypes } from '../../../../components/Toast/Toast.types';
import { useGetRecipeBases } from '../../../../services/base/base.service';
import { Base } from '../../../../services/base/base.types';
import { useGetClasses } from '../../../../services/class/class.service';
import { Class } from '../../../../services/class/class.types';
import { useGetRecipeProducts } from '../../../../services/product/product.service';
import { Product } from '../../../../services/product/product.types';
import { useGetRecipeSizes } from '../../../../services/size/size.service';
import { Size } from '../../../../services/size/size.types';
import { useGetSpecs, useSpecsInRecipe } from '../../../../services/spec/spec.service';
import { useFetchErrorToast } from '../../../../utils/useFetchErrorToasts/useFetchErrorToast';
import {
  ButtonContainer,
  ButtonWrapper,
  DeleteButton,
  DropdownContainer,
  RadioWrapper,
  RowWrapper,
  SelectSpecContainer,
  TableRowWrapper,
  TableWrapper,
} from './SelectSpec.styles';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { SelectSpecProps } from './SelectSpec.types';
import useTreeViewRowStates from '../../../../components/MenuTreeView/useMenuTreeViewRowStates';
import getDataStructure from '../../../../utils/dataStructure/dataStructure';
import { EditCell } from '../../../../components/Table/EditCellRenderer/EditCellRenderer.styles';
import { ChangeSpecsStep, CombinedSpec } from "../ChangeSpecs.types";

const SelectSpec: FC<SelectSpecProps> = ({
                                             selectedSpecs,
                                             setSelectedSpecs,
                                             selectedClass,
                                             setSelectedClass,
                                             selectedBase,
                                             setSelectedBase,
                                             selectedSize,
                                             setSelectedSize,
                                             selectedProduct,
                                             setSelectedProduct,
                                             setStep
                                         }) =>
{
  const theme = useTheme();
  const currentGridRef = useRef<AgGridReact>(null);
  const desiredGridRef = useRef<AgGridReact>(null);

  const [isRecipes, setIsRecipes] = useState<boolean>(true);
  const [searchActive, setSearchActive] = useState<boolean>(false);

  const [currentSpec, setCurrentSpec] = useState<any[]>([]);
  const [desiredSpec, setDesiredSpec] = useState<any[]>([]);

  // Fetch classes and map to dropdown options
  const { data: classes, isError: classesError } = useGetClasses();
  const classOptions = useMemo(
    () => classes?.data?.map((item: Class) => {
      return { value: item?.classNo?.toString(), label: `${item?.classNo} ~ ${item.description}` };
    }), [classes?.data]);

  // Fetch bases and map to dropdown options
  const { data: bases, isError: basesError } = useGetRecipeBases(
    { classNo: selectedClass?.toString() || '' },
    selectedClass !== undefined
  );

  const baseOptions = useMemo(
    () => bases?.data?.map((item: Base) => {
      return { value: item?.baseNo?.toString(), label: `${item?.baseNo} ~ ${item.description}` };
    }),[bases?.data]);

  // Fetch sizes and map to dropdown options
  const { data: sizes, isError: sizesError } = useGetRecipeSizes(
    { classNo: selectedClass?.toString() || '', baseNo: selectedBase?.toString() || '' },
    selectedClass !== undefined && selectedBase !== undefined
  );
  const sizeOptions = useMemo(
    () => sizes?.data?.map((item: Size) => {
      return { value: item?.sizeNo?.toString(), label: `${item?.sizeNo} ~ ${item.description}` };
    }), [sizes?.data]
  );

  // Fetch products and map to dropdown options
  const { data: products, isError: productsError } = useGetRecipeProducts(
    {
      classNo: selectedClass?.toString() || '',
      baseNo: selectedBase?.toString() || '',
      sizeNo: selectedSize?.toString() || '',
    },
    selectedClass !== undefined && selectedBase !== undefined && selectedSize !== undefined
  );
  const productOptions = useMemo(
    () => products?.data?.map((item: Product) => {
      return { value: item?.productNo?.toString(), label: `${item?.productNo} ~ ${item.description}` };
    }), [products?.data]
  );

  const { data: specsInRecipe, isError: specsInRecipeError } = useSpecsInRecipe(
    {
      classNo: selectedClass?.toString(),
      baseNo: selectedBase?.toString(),
      sizeNo: selectedSize?.toString(),
      productNo: selectedProduct?.toString(),
    },
    isRecipes && searchActive
  );

  const { data: specsInSubrecipe } = useGetSpecs(
    {
      isSubRecipe: true,
    },
    !isRecipes && searchActive
  );

  useFetchErrorToast([{isError: specsInRecipeError, label: 'Error fetching specs', id: 'specsFetchError'}]);
  
  const handleRadioChange = () => {
    if (isRecipes) {
      setIsRecipes(!isRecipes);
      setSearchActive(true);
    } else {
      setIsRecipes(!isRecipes);
      setSearchActive(false);
    }
  };

  const handleSearchClick = () => {
    setSearchActive(true);
  };

  const cleanDataFiltered = () => {
    setCurrentSpec([]);
    setDesiredSpec([]);
  };

  const handleClassSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSearchActive(false);
    setSelectedClass(+event.target.value);
    cleanDataFiltered();
  };

  const handleBaseSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSearchActive(false);
    setSelectedBase(+event.target.value);
    cleanDataFiltered();
  };

  const handleSizeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSearchActive(false);
    setSelectedSize(+event.target.value);
    cleanDataFiltered();
  };

  const handleProductSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSearchActive(false);
    setSelectedProduct(+event.target.value);
    cleanDataFiltered();
  };

  const onSelectionChangedCurrent = (event: SelectionChangedEvent) => {
    setCurrentSpec(event.api.getSelectedRows());
  };

  const onSelectionChangedDesired = (event: SelectionChangedEvent) => {
    setDesiredSpec(event.api.getSelectedRows());
  };

  const { resetLocalStorage } = useTreeViewRowStates(getDataStructure(''), 'selectMenusToChangeSpec', false);

  const handleNextClick = () => {
      resetLocalStorage();
      setStep(ChangeSpecsStep.SelectMenus);
  };

  const selectCurrentSpec = (selectedSpecs: any, desiredSpec: any) => {
    currentGridRef?.current?.api?.forEachNode((node) => {
      const matchItem = selectedSpecs.find((selectedSpec: any) => selectedSpec.specNo === node?.data.specNo);
      if (matchItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });

    desiredGridRef?.current?.api?.forEachNode((node) => {
      const matchItem = desiredSpec.find((desiredSpec: any) => desiredSpec.specNo === node?.data.specNo);
      if (matchItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  };


  useEffect(() => {
    if (currentSpec) {
      selectCurrentSpec(currentSpec, desiredSpec);
    }
  });

  const handleCombineSelections = () => {
    if (currentSpec.some((value) => value.specNo === desiredSpec?.[0]?.specNo)) {
      showToast({
        label: 'Selected specs cannot be the same.',
        subtext: 'Please select a different spec to change.',
        id: 'selectSpecsToChangeError',
        toastType: ToastTypes.Error,
      });
    } else {
      const combined: CombinedSpec[] = [...selectedSpecs];
      currentSpec.forEach((current) => {
        desiredSpec.forEach((desired) => {
          if (!combined.some((spec) =>
            spec.currentSpecNo === current.specNo &&
            spec.currentDescription === current.description &&
            spec.desiredSpecNo === desired.specNo &&
            spec.desiredDescription === desired.description
          )) {
            combined.push({
              currentSpecNo: current.specNo,
              currentDescription: current.description,
              desiredSpecNo: desired.specNo,
              desiredDescription: desired.description,
            });
          }
        });
      });
      setSelectedSpecs(combined);
      resetLocalStorage();
    }
  }

  const RemoveRowData = (params: any) => {
    return (
      <EditCell>
        <DeleteButton data-testid='deleteSpecButton'>
          <DeleteOutlinedIcon
            fontSize='inherit'
            htmlColor={theme.colors.red}
            style={{cursor: 'pointer', height: '50%'}}
            onClick={() => {
              const updatedSpecs = selectedSpecs.filter(
                  (spec) => !(spec.currentSpecNo === params.data.currentSpecNo && spec.desiredSpecNo === params.data.desiredSpecNo)
              );
              setSelectedSpecs(updatedSpecs);
            }}
          />
        </DeleteButton>
      </EditCell>
    );
  };

  return (
    <SelectSpecContainer>
      <H2>Select Recipe Type</H2>
      <RowWrapper>
        <RadioWrapper>
          <Radio
            label='Recipes'
            id='recipes'
            checked={isRecipes}
            onChange={handleRadioChange}
            data-testid='RecipesRadio'
          ></Radio>
        </RadioWrapper>
        <RadioWrapper>
          <Radio
            label='Option Recipes'
            id='optionRecipes'
            checked={!isRecipes}
            onChange={handleRadioChange}
            data-testid='OptionRecipesRadio'
          ></Radio>
        </RadioWrapper>
      </RowWrapper>
      {isRecipes && (
        <>
          <H2>Select Menu Item</H2>
            <RowWrapper>
            <DropdownWrapperMedium>
              <DropdownContainer>
              <Dropdown
              id='selectedClass'
              label='Class'
              options={classOptions}
              value={selectedClass?.toString()}
              onChange={handleClassSelect}
              optionsPlaceholder='Select Class'
              dataTestId={'ClassDropdown'}
              errorText={classesError ? 'Error fetching classes' : undefined} 
              />
              </DropdownContainer>
            </DropdownWrapperMedium>
            <DropdownWrapperMedium>
              <DropdownContainer>
              <Dropdown
              id='selectedBase'
              label='Base'
              options={baseOptions}
              value={selectedBase?.toString()}
              onChange={handleBaseSelect}
              optionsPlaceholder='Select Base'
              dataTestId={'BaseDropdown'}
              errorText={basesError ? 'Error fetching bases' : undefined} 
              />
              </DropdownContainer>
            </DropdownWrapperMedium>
            <DropdownWrapperMedium>
              <DropdownContainer>
              <Dropdown
              id='selectedSize'
              label='Size'
              options={sizeOptions}
              value={selectedSize?.toString()}
              onChange={handleSizeSelect}
              optionsPlaceholder='Select Size'
              dataTestId={'SizeDropdown'}
              errorText={sizesError ? 'Error fetching sizes' : undefined} 
              />
              </DropdownContainer>
            </DropdownWrapperMedium>
            <DropdownWrapperMedium>
              <DropdownContainer>
              <Dropdown
              id='selectedProduct'
              label='Product'
              options={productOptions}
              value={selectedProduct?.toString()}
              onChange={handleProductSelect}
              optionsPlaceholder='Select Product'
              dataTestId={'ProductDropdown'}
              errorText={productsError ? 'Error fetching products' : undefined} 
              />
              </DropdownContainer>
            </DropdownWrapperMedium>
            <ButtonWrapper>
              <Button
              label='Search'
              testId='searchSpecsButton'
              onClick={handleSearchClick}
              disabled={
                selectedClass === undefined ||
                selectedBase === undefined ||
                selectedSize === undefined ||
                selectedProduct === undefined
              }
              />
              </ButtonWrapper>
            </RowWrapper>
        </>
      )}
      <H2>Available Specs</H2>
      <TableRowWrapper>
        <TableWrapper>
          <Table
            ref={currentGridRef}
            showHeader={true}
            headerTitle='Current Spec'
            floatingFiltersHeight={0}
            rowData={isRecipes ? specsInRecipe?.data || [] : specsInSubrecipe?.data || []}
            columnDefs={[
              {
                minWidth: 70,
                maxWidth: 70,
                flex: 1,
                field: '',
                headerName: 'Select',
                checkboxSelection: true,
                sortable: false,
              },
              { minWidth: 110, flex: 1, field: 'specNo', headerName: 'Spec' },
              { minWidth: 320, flex: 2, field: 'description', headerName: 'Description', resizable: false },
            ]}
            rowSelection='multiple'
            suppressRowClickSelection={false}
            rowMultiSelectWithClick={true}
            onSelectionChanged={onSelectionChangedCurrent}
            noRowsMessage='Select a Menu Item to see Specs'
            returnedRecords={isRecipes ? specsInRecipe?.data?.length || 0 : specsInSubrecipe?.data?.length || 0}
          />
        </TableWrapper>
        <ChevronRightOutlinedIcon htmlColor={theme.colors.gray400} />
        <TableWrapper>
          <Table
            ref={desiredGridRef}
            showHeader={true}
            headerTitle='Desired Spec'
            floatingFiltersHeight={0}
            rowData={isRecipes ? specsInRecipe?.data : specsInSubrecipe?.data}
            columnDefs={[
              {
                minWidth: 70,
                maxWidth: 70,
                flex: 1,
                field: '',
                headerName: 'Select',
                checkboxSelection: true,
                sortable: false,
              },
              { minWidth: 110, flex: 1, field: 'specNo', headerName: 'Spec' },
              { minWidth: 320, flex: 2, field: 'description', headerName: 'Description', resizable: false },
            ]}
            rowSelection='single'
            onSelectionChanged={onSelectionChangedDesired}
            noRowsMessage='Select a Menu Item to see Specs'
            returnedRecords={isRecipes ? specsInRecipe?.data?.length || 0 : specsInSubrecipe?.data?.length || 0}
          />
        </TableWrapper>
      </TableRowWrapper>
      <ButtonContainer>
        <Button label='Select' testId='selectSpecsButton' onClick={handleCombineSelections} disabled={ currentSpec.length === 0 && desiredSpec.length === 0} />
      </ButtonContainer>
      <TableRowWrapper>
        <Table
          headerTitle='Selected Specs'
          showHeader={true}
          columnDefs={
            [
              { minWidth: 110, flex: 1, field: 'currentSpecNo', headerName: 'Current Spec' },
              { minWidth: 110, flex: 1, field: 'currentDescription', headerName: 'Current Description' },
              { minWidth: 110, flex: 1, field: 'desiredSpecNo', headerName: 'Desired Spec' },
              { minWidth: 110, flex: 1, field: 'desiredDescription', headerName: 'Desired Description' },
              {
                minWidth: 70,
                maxWidth: 70,
                flex: 1,
                field: 'remove',
                headerName: 'Remove',
                resizable: false,
                cellRenderer: RemoveRowData,
                floatingFilter: false,
              }
            ]
          }
          rowData={selectedSpecs}
          returnedRecords={selectedSpecs.length}
        />
      </TableRowWrapper>
      <ButtonContainer>
        <Button
          label='Next'
          testId='nextStepButton'
          onClick={handleNextClick}
          disabled={selectedSpecs.length === 0}
        />
      </ButtonContainer>
    </SelectSpecContainer>
  );
};

export default SelectSpec;